import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { StepProps, ApiTemplate } from '@/src/types';
import { cn } from '@/src/infrastructure/lib/utils';
import { motion } from 'motion/react';
import { hostFacingCopy } from '@/src/modules/pages/hostFacingCopy';
import { FullScreenPreviewDialog } from './FullScreenPreviewDialog';
import type { DraftPreviewData } from './DraftPreviewModal';
import { getTemplates } from '@/src/infrastructure/lib/api';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { TemplateCardPreview } from '@/src/modules/renderer/TemplateCardPreview';
import { LiveBadge, WowPreviewCard } from '@/src/modules/renderer/WowPreviewCard';
import { getTemplatePreview, isWowTemplate } from '@/src/modules/renderer/templatePreviews';
import { LoopSlotProvider, createSlotScheduler } from '@/src/modules/renderer/livePreviewScheduler';
import { loopConcurrencyCap } from '@/src/modules/renderer/mediaPolicy';
import type { ApiInvitation } from '@/src/types';
import { CinematicWeddingExperience } from '@/src/modules/experiences/wedding/CinematicWeddingExperience';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';
import { trackEvent } from '@/src/infrastructure/lib/analytics';
import { switchTemplate, type SwitchReport } from '@/src/modules/wizard/schema/switch-template';
import { SwitchTemplateDialog } from './SwitchTemplateDialog';
import { trackTemplateChange } from '@/src/infrastructure/lib/behaviour-analytics';
import { publicAssetUrl } from '@/src/infrastructure/media';

/**
 * CinematicWedding — immersive multi-section wedding experience template.
 * DB id: 'tpl_cinematic_wedding_001' (migration 20260606000000_fix_cinematic_wedding_id_add_lagna_patrika)
 * Frontend registry: CinematicWedding/index.tsx
 *
 * NOTE: The old id 'tpl_wedding_classic_001' / 'seed-wedding-classic-coral-sky-1' is
 * retired. This is the correct canonical id going forward.
 */
const CINEMATIC_WEDDING_TEMPLATE: ApiTemplate = {
  id: 'tpl_cinematic_wedding_001',
  slug: 'cinematic-wedding-experience',
  name: 'Cinematic Wedding Experience',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'Immersive wedding microsite with couple storytelling, family tree, multiple events with maps, pre-wedding media gallery, and RSVP.',
  tags: ['cinematic', 'wedding', 'storytelling', 'premium', 'experience'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: true,
    supportsMap: true,
    supportsMultipleEvents: true,
    experienceRenderer: 'cinematic-wedding',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Dev21 — a code-rendered React experience template. It is not stored as a
 * JSON schema, so it is offered here as a fixed catalogue entry; rendering is
 * delegated to the frontend template `registry` by `id`.
 */
const WEDDING_PLOT_PLANE_TEMPLATE: ApiTemplate = {
  id: 'exp-wedding-plot-plane-3d',
  slug: 'exp-wedding-plot-plane-3d',
  name: '3D Plot Plane Motion',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/exp-wedding-plot-plane-3d/01-couple-sangeet-wide.jpg',
  isPremium: true,
  isActive: true,
  description:
    'Your ceremony photos, flown through six stacked planes. Empty scenes keep the sample plates.',
  tags: ['wedding', '3d', 'webgl', 'scroll', 'photography', 'sangeet', 'mehndi', 'pheras'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: true,
    supportsMap: true,
    supportsMultipleEvents: true,
    experienceRenderer: 'exp-wedding-plot-plane-3d',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const ROYAL_GANESH_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'royal-ganesh-interactive',
  slug: 'royal-ganesh-interactive',
  name: 'Royal Ganesh — Interactive',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/royal-ganesh-interactive/extracted/logo/logo.png',
  isPremium: true,
  isActive: true,
  description:
    'A premium, animated, interactive wedding invitation featuring Ganesha, custom floral motifs, and a countdown.',
  tags: ['wedding', 'ganesh', 'interactive', 'animated', 'premium', 'royal', 'blue'],
  capabilities: { experienceRenderer: 'royal-ganesh-interactive' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/** October 2026 competitor-inspired suite — new ids only (template immutability). */
const VEDIC_MANDIR_DIVINE_TEMPLATE: ApiTemplate = {
  id: 'exp-vedic-mandir-divine',
  slug: 'exp-vedic-mandir-divine',
  name: 'Vedic Mandir Divine',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'Sacred temple-inspired wedding microsite: saffron, maroon and gold, multi-ceremony schedule, diya ambience, RSVP and maps.',
  tags: ['wedding', 'temple', 'vedic', 'sacred', 'saffron', 'multi-ceremony', 'premium', 'hindu'],
  capabilities: { experienceRenderer: 'exp-vedic-mandir-divine', supportsRSVP: true, supportsMap: true, supportsMultipleEvents: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EMERALD_NOIR_RECEPTION_TEMPLATE: ApiTemplate = {
  id: 'exp-emerald-noir-reception',
  slug: 'exp-emerald-noir-reception',
  name: 'Emerald Noir Reception',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'After-dark cocktail & reception invite: obsidian, emerald and gold foil, wax-seal unboxing gate, glass cards and RSVP.',
  tags: ['wedding', 'reception', 'cocktail', 'dark', 'emerald', 'luxury', 'premium', 'evening'],
  capabilities: { experienceRenderer: 'exp-emerald-noir-reception', supportsRSVP: true, supportsMap: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const SCRATCH_STD_GOLDENSAND_TEMPLATE: ApiTemplate = {
  id: 'exp-scratch-std-goldensand',
  slug: 'exp-scratch-std-goldensand',
  name: 'Golden Sand — Scratch Save the Date',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'Interactive Save-the-Date: scratch gold sand to reveal couple names and date, with countdown and share CTA.',
  tags: ['wedding', 'save-the-date', 'scratch', 'interactive', 'canvas', 'teaser', 'premium'],
  capabilities: { experienceRenderer: 'exp-scratch-std-goldensand', supportsCountdown: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const DEV21_WEDDING_TEMPLATE: ApiTemplate = {
  id: 'dev21',
  slug: 'dev21-modern-premium',
  name: 'Dev21 — Modern Premium',
  occasionType: 'WEDDING',
  previewImageUrl: null,
  isPremium: true,
  isActive: true,
  description:
    'A fresh, cinematic single-page wedding invitation: parallax hero, animated story timeline, masonry gallery, teaser video, family tree, schedule, live map and a validated RSVP.',
  tags: ['modern', 'premium', 'animated', 'glassmorphism', 'responsive'],
  capabilities: { experienceRenderer: 'dev21' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * LagnaPatrikaInteractive — a code-rendered React experience template.
 * Features: traditional marigold garlands, floating rose petals, 3D tilt effects,
 * and built-in English/Marathi translation toggles.
 */
const LAGNA_PATRIKA_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'lagna-patrika-interactive',
  slug: 'lagna-patrika-interactive',
  name: 'Lagna Patrika - Interactive',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1542451313056-b7c8e626645f?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'An elegant, interactive Lagna Patrika featuring Marathi/English bilingual toggles, traditional mandap designs, and responsive layouts.',
  tags: ['interactive', 'traditional', 'marathi', 'bilingual', 'animated', 'premium'],
  capabilities: { experienceRenderer: 'lagna-patrika-interactive' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

// Restored 2026-10-24: these five templates are live on main/prod and seeded
// on develop, but their components were dropped from develop by the 2026-08-25
// merge 37578f8c (landed via #261). Kept identical to main.
const VIDEO_ROYAL_01_TEMPLATE: ApiTemplate = {
  id: 'video-01-royal-gold',
  slug: 'video-01-royal-gold',
  name: 'Royal Gold Video — Animated Ceremonies',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-01-royal/board_frame.png',
  isPremium: true,
  isActive: true,
  description: 'A breathtaking, cinematic Indian wedding invitation powered by real-time video backgrounds, floral board framing, ceremony caricature art, and dynamic typography.',
  tags: ['video', 'royal', 'gold', 'cinematic', 'animated', 'premium', 'scroll-experience', 'marathi', 'wedding', 'haldi', 'mehendi', 'sangeet', 'reception', 'vocal', 'wow'],
  capabilities: { experienceRenderer: 'video-01-royal-gold', supportsRSVP: true, supportsCountdown: true, supportsMusic: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const VIDEO_KIRTAN_DIVINE_TEMPLATE: ApiTemplate = {
  id: 'video-02-kirtan-divine',
  slug: 'video-02-kirtan-divine',
  name: 'Radha Krishna Kirtan — Divine Video Invitation',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-02-kirtan-divine/ganesh_header.png',
  isPremium: true,
  isActive: true,
  description: 'A divine, cinematic Radha-Krishna Kirtan and wedding invitation featuring peacock artwork framing, Shree Ganesha header, real-time motion background, background audio, and dynamic typography.',
  tags: ['video', 'kirtan', 'krishna', 'radha', 'radha-krishna', 'devotional', 'wedding', 'marathi', 'hindi', 'peacock', 'god', 'divine-love', 'cinematic', 'scroll-experience', 'animated', 'wow'],
  capabilities: { experienceRenderer: 'video-02-kirtan-divine', supportsRSVP: true, supportsCountdown: true, supportsMusic: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const VIDEO_RADHA_KRISHNA_TEMPLATE: ApiTemplate = {
  id: 'video-03-radha-krishna',
  slug: 'video-03-radha-krishna',
  name: 'Radha Krishna Garden — PSD Video Invitation',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-03-radha-krishna/mandap-forest.webp',
  isPremium: true,
  isActive: true,
  description: 'A PSD-derived Radha Krishna garden wedding invitation with cinematic scroll scenes, floral and gold particle motion overlays, dynamic ceremony details, photo showcase cards, RSVP, and map directions.',
  tags: ['video', 'radha', 'krishna', 'radha-krishna', 'garden', 'mandap', 'devotional', 'wedding', 'haldi', 'sangeet', 'reception', 'cinematic', 'scroll-experience', 'animated', 'wow'],
  capabilities: { experienceRenderer: 'video-03-radha-krishna', supportsRSVP: true, supportsCountdown: true, supportsMusic: false, supportsMap: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_GSAP_RADHA_KRISHNA_TEMPLATE: ApiTemplate = {
  id: 'exp-gsap-radha-krishna',
  slug: 'exp-gsap-radha-krishna',
  name: 'Radha Krishna GSAP Scroll Divine',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-03-radha-krishna/mandap-forest.webp',
  isPremium: true,
  isActive: true,
  description: 'An opulent GSAP ScrollTrigger scroll-driven traditional Radha Krishna wedding invitation featuring 3D perspective animations, floating divine lotus and peacock feather parallax, pinned ritual scenes, and single scroll ergonomics.',
  tags: ['radha-krishna', 'gsap', 'scroll-driven', 'traditional', 'wedding', 'hindu-gods', 'divine', 'cinematic', '3d', 'scroll-experience', 'ai-native', 'krishna', 'radha', 'mandap', 'haldi', 'phere'],
  capabilities: { experience: true, aiNative: true, stylePack: 'heritage', supportsRSVP: true, experienceRenderer: 'exp-gsap-radha-krishna', supportsMap: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_GSAP_SHIVA_PARVATI_TEMPLATE: ApiTemplate = {
  id: 'exp-gsap-shiva-parvati',
  slug: 'exp-gsap-shiva-parvati',
  name: 'Shiva Parvati Divine Marriage',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/exp-shiva-parvati/shiva_parvati_hero_bg.jpg',
  isPremium: true,
  isActive: true,
  description: 'A majestic GSAP ScrollTrigger scroll-driven traditional Hindu gods invitation celebrating the divine marriage of Lord Shiva & Goddess Parvati on Mount Kailash with 3D perspective animations and sacred floating accents.',
  tags: ['shiva-parvati', 'gsap', 'scroll-driven', 'traditional', 'wedding', 'hindu-gods', 'divine', 'kailash', 'om-namah-shivaya', 'damru', 'trishul', 'sacred-matrimony', 'cinematic', '3d', 'scroll-experience', 'ai-native', 'shiva', 'parvati', 'mandap', 'tilak', 'phere'],
  capabilities: { experience: true, aiNative: true, stylePack: 'heritage', supportsRSVP: true, experienceRenderer: 'exp-gsap-shiva-parvati', supportsMap: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * TraditionalWeddingInteractive — rich interactive wedding invitation.
 * Features: couple intro, multi-event timeline, expandable family tree,
 * family invitation cards, and integrated RSVP.
 */
// Artistic wedding templates (ported 2026-10-24 from feature/artistic-wedding-templates).
const EXP_WEDDING_INDO_GHIBLI_TEMPLATE: ApiTemplate = {
  id: 'exp-wedding-indo-ghibli-v1',
  slug: 'exp-wedding-indo-ghibli-v1',
  name: 'Indo-Ghibli Anime Wedding',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/ghibli/ghibli_palace_hero.jpg',
  isPremium: true,
  isActive: true,
  description: 'Hand-painted Studio Ghibli gouache anime wedding experience with golden hour palace balcony, lush gardens, and watercolor aesthetics.',
  tags: ['cinematic', 'wedding', 'ghibli', 'anime', 'miyazaki', 'watercolor', 'handpainted', 'premium', 'scroll-experience', 'ai-native'],
  capabilities: { experience: true, aiNative: true, stylePack: 'indo_ghibli', supportsRSVP: true, supportsCalendar: true, experienceRenderer: 'exp-wedding-indo-ghibli-v1' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_WEDDING_PICHWAI_TEMPLATE: ApiTemplate = {
  id: 'exp-wedding-pichwai-v1',
  slug: 'exp-wedding-pichwai-v1',
  name: 'Royal Pichwai Heritage Wedding',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/pichwai/pichwai_palace_hero.jpg',
  isPremium: true,
  isActive: true,
  description: 'Authentic Mewar royal court & Pichwai miniature painting with gold filigree borders, peacocks, lotuses, and midnight indigo sky.',
  tags: ['cinematic', 'wedding', 'pichwai', 'miniature', 'rajput', 'heritage', 'gold-foil', 'lotus', 'peacock', 'premium', 'scroll-experience', 'ai-native'],
  capabilities: { experience: true, aiNative: true, stylePack: 'pichwai_heritage', supportsRSVP: true, supportsCalendar: true, experienceRenderer: 'exp-wedding-pichwai-v1' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_WEDDING_STORYBOOK_3D_TEMPLATE: ApiTemplate = {
  id: 'exp-wedding-storybook-3d-v1',
  slug: 'exp-wedding-storybook-3d-v1',
  name: 'Storybook 3D Pixar Wedding',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/storybook/storybook_palace_hero.jpg',
  isPremium: true,
  isActive: true,
  description: 'Whimsical 3D animated fairy-tale wedding with warm Pixar-style lighting, glowing lanterns, and romantic storybook courtyard.',
  tags: ['cinematic', 'wedding', 'storybook', '3d', 'pixar', 'disney', 'animated', 'fairytale', 'lanterns', 'premium', 'scroll-experience', 'ai-native'],
  capabilities: { experience: true, aiNative: true, stylePack: 'storybook_3d', supportsRSVP: true, supportsCalendar: true, experienceRenderer: 'exp-wedding-storybook-3d-v1' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

// 3D wedding templates (ported 2026-10-31 from feature/artistic-wedding-templates).
// Registry-backed three.js experiences; both use the standard wedding Details form
// (see wizard-routing STANDARD_DETAILS_TEMPLATE_IDS) and show a static poster card.
const MARATHI_AKSHATA_3D_TEMPLATE: ApiTemplate = {
  id: 'marathi-akshata-3d',
  slug: 'marathi-akshata-3d',
  name: 'Marathi Akshata 3D Wedding',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/marathi-akshata-3d/poster.jpg',
  isPremium: true,
  isActive: true,
  description: 'An interactive 3D Marathi wedding: guests shower akshata on the couple, lower the antarpat for the first glance, then find your date, venue and RSVP.',
  tags: ['wedding', 'marathi', 'traditional', '3d', 'interactive', 'animated', 'akshata', 'premium'],
  capabilities: { supportsRSVP: true, supportsGallery: false, supportsMap: true, experienceRenderer: 'marathi-akshata-3d' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const KRISHNA_MEERA_3D_DIORAMA_TEMPLATE: ApiTemplate = {
  id: 'exp-krishna-meera-3d-diorama',
  slug: 'exp-krishna-meera-3d-diorama',
  name: 'Krishna–Meera in Vrindavan 3D',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/exp-krishna-meera-3d-diorama/poster.webp',
  isPremium: true,
  isActive: true,
  description: 'A living 3D Vrindavan at golden hour: Krishna plays his flute beside Meera with her ektara under the kadamba tree, then a second scene at the Yamuna ghat at dusk where they exchange varmalas under a marigold mandap. Your names, date, venue and RSVP sit over both scenes.',
  tags: ['3d', 'immersive', 'krishna', 'meera', 'diorama', 'premium', 'wedding', 'vrindavan', 'varmala'],
  capabilities: { supportsRSVP: true, supportsGallery: false, supportsMap: false, experienceRenderer: 'exp-krishna-meera-3d-diorama' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const TRADITIONAL_WEDDING_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'traditional-wedding-interactive',
  slug: 'traditional-wedding-interactive',
  name: 'Traditional Wedding — Interactive',
  occasionType: 'WEDDING',
  previewImageUrl:
    'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'Rich interactive wedding invitation: couple introduction, multi-event timeline, expandable family tree, family invitation cards, and integrated RSVP.',
  tags: ['wedding', 'traditional', 'interactive', 'family-tree', 'timeline', 'premium'],
  capabilities: { experienceRenderer: 'traditional-wedding-interactive' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * StitchImmersiveWedding — a macOS-inspired glassmorphism wedding experience.
 * Features: parallax wallpaper, animated dock, cycling name greeter, floating glass windows.
 */
const STITCH_IMMERSIVE_WEDDING_TEMPLATE: ApiTemplate = {
  id: 'stitch-immersive-wedding',
  slug: 'stitch-immersive-wedding',
  name: 'Stitch Immersive Wedding',
  occasionType: 'WEDDING',
  previewImageUrl:
    'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'A macOS-inspired glassmorphism wedding invitation with immersive parallax background, animated dock navigation, and floating glass windows.',
  tags: ['glassmorphism', 'macos', 'immersive', 'animated', 'wedding', 'premium'],
  capabilities: { experienceRenderer: 'stitch-immersive-wedding' },
  version: '1.0.0',
  renderMode: 'json-renderer',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * MarathiWeddingClassic — a beautiful traditional Marathi wedding template.
 * Features: Type B manifest-driven design, traditional motifs, animations.
 */
const MARATHI_WEDDING_CLASSIC_TEMPLATE: ApiTemplate = {
  id: 'marathi-wedding-classic',
  slug: 'marathi-wedding-classic',
  name: 'Marathi Wedding Classic',
  occasionType: 'WEDDING',
  previewImageUrl: publicAssetUrl('type-b/harw/bg.png'),
  isPremium: true,
  isActive: true,
  description: 'A beautiful traditional Marathi wedding template generated with Type B pipeline.',
  tags: ['marathi', 'traditional', 'type-b', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsMap: true,
    experienceRenderer: 'marathi-wedding-classic',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * MarathiWeddingInteractive — a bilingual Marathi wedding invitation.
 * Features: 3D parallax, falling marigold petals, glowing sways, translation.
 */
const MARATHI_WEDDING_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'marathi-wedding-interactive',
  slug: 'marathi-wedding-interactive',
  name: 'Marathi Wedding Interactive',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/marathi/backdrop.jpg',
  isPremium: true,
  isActive: true,
  description: 'A premium interactive Lagna Patrika featuring a Marathi/English bilingual translation toggle, gentle 3D parallax scrolling, and falling gold marigold petals.',
  tags: ['marathi', 'traditional', '3d', 'interactive', 'animated', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsMap: true,
    experienceRenderer: 'marathi-wedding-interactive',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const SOUTH_INDIAN_TRADITION_TEMPLATE: ApiTemplate = {
  id: 'south-indian-tradition-experience',
  slug: 'south-indian-tradition-experience',
  name: 'South Indian Tradition — Tamil/Telugu/Kannada',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A traditional South Indian wedding: temple gopuram hero, animated diyas, ceremony programme with tilak, mangala sutra, and reception.',
  tags: ['south-indian', 'tamil', 'telugu', 'kannada', 'temple', 'traditional', 'animated'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: false,
    supportsMap: true,
    experienceRenderer: 'south-indian-tradition-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const NIKAH_ELEGANT_TEMPLATE: ApiTemplate = {
  id: 'nikah-elegant-experience',
  slug: 'nikah-elegant-experience',
  name: 'NikahElegant — Elegant Muslim Wedding',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A refined Muslim Nikah invitation: Bismillah header, animated geometric mandala, family details, ceremony & walima events, and a clear RSVP.',
  tags: ['nikah', 'muslim', 'elegant', 'arabic', 'animated', 'gold'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: false,
    supportsMap: true,
    experienceRenderer: 'nikah-elegant-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const SIKH_ANAND_KARAJ_TEMPLATE: ApiTemplate = {
  id: 'exp-sikh-anand-karaj',
  slug: 'sikh-anand-karaj',
  name: 'Sikh Anand Karaj — Sacred Journey',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'Majestic Sikh Punjabi wedding microsite with 8 parallax chapters: Waheguru soft gate with Ik Onkar crest, mustard field hero, Phulkari family gratitude letter, twilight Gurdwara save-the-date, multi-event schedule with dress codes, Gurdwara etiquette, Khanda sacred union ceremony, and RSVP blessings wall.',
  tags: ['wedding', 'sikh', 'punjabi', 'anand-karaj', 'phulkari', 'gurdwara', 'parallax', 'sacred', 'dress-code', 'premium', 'khanda'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: true,
    supportsCountdown: true,
    supportsMusic: true,
    supportsMap: true,
    supportsMultipleEvents: true,
    supportsDressCode: true,
    experienceRenderer: 'exp-sikh-anand-karaj',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * LakePalaceDestination — scroll-scrubbed destination-wedding plot-plane
 * experience (misty lake → jetty → courtyard → function arcade → RSVP terrace).
 * Id is shared with the Prisma migration
 * 20261015000000_add_lake_palace_destination_template and the frontend registry.
 */
const LAKE_PALACE_DESTINATION_TEMPLATE: ApiTemplate = {
  id: 'lake-palace-destination',
  slug: 'lake-palace-destination',
  name: 'Lake Palace Destination',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/lake-palace-destination/preview.svg',
  isPremium: true,
  isActive: true,
  description:
    'A destination-wedding journey across a misty lake: boat arrival, marble jetty, palace courtyard, function arcade over water, and an RSVP terrace with map and travel notes.',
  tags: ['wedding', 'destination', 'lake-palace', 'plot-plane', 'cinematic', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: true,
    supportsMap: true,
    supportsMultipleEvents: true,
    supportsCountdown: false,
    supportsMusic: false,
    experienceRenderer: 'lake-palace-destination',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Cinematic Royal — AI-native Type-A experience template.
 * Generates personalised ceremony images via Gemini and weaves them into
 * a scroll-driven regal gold & ivory wedding experience.
 */
const EXP_CINEMATIC_ROYAL_TEMPLATE: ApiTemplate = {
  id: 'exp-cinematic-royal-v1',
  slug: 'exp-cinematic-royal-v1',
  name: 'Cinematic Royal',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/wow/gemini-wedding-hero.png',
  isPremium: true,
  isActive: false,
  description: 'Regal gold and ivory wedding experience with scroll-driven ceremony sections, interactive couple storytelling, and RSVP.',
  tags: ['cinematic', 'wedding', 'royal', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'royal', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_WEDDING_RAJASTHANI_ROYAL_TEMPLATE: ApiTemplate = {
  id: 'exp-wedding-rajasthani-royal-v1',
  slug: 'exp-wedding-rajasthani-royal-v1',
  name: 'Rajasthani Rajput Royal',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/wow/rajasthani_palace_bg.jpg',
  isPremium: true,
  isActive: true,
  description: 'Grand Rajasthani palace wedding experience with rich crimson, warm gold, traditional Rajput motifs, and event timeline.',
  tags: ['cinematic', 'wedding', 'rajasthani', 'rajput', 'royal', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'heritage', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_CINEMATIC_MINIMAL_TEMPLATE: ApiTemplate = {
  id: 'exp-cinematic-minimal-v1',
  slug: 'exp-cinematic-minimal-v1',
  name: 'Cinematic Minimal',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/marathi/backdrop.jpg',
  isPremium: true,
  isActive: false,
  description: 'Clean white and charcoal minimal wedding experience with elegant typography and subtle parallax scrolling.',
  tags: ['cinematic', 'wedding', 'minimal', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'minimal', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_CINEMATIC_FANTASY_TEMPLATE: ApiTemplate = {
  id: 'exp-cinematic-fantasy-v1',
  slug: 'exp-cinematic-fantasy-v1',
  name: 'Cinematic Fantasy',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/marathi/peacock.jpg',
  isPremium: true,
  isActive: false,
  description: 'Enchanted wedding experience with deep jewel tones, vibrant purple and teal hues, and scroll-driven parallax.',
  tags: ['cinematic', 'wedding', 'fantasy', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'fantasy', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

export const EXPERIENCE_STYLE_BY_TEMPLATE_ID: Record<string, string> = {
  'exp-cinematic-royal-v1': 'royal',
  'exp-cinematic-minimal-v1': 'minimal',
  'exp-cinematic-fantasy-v1': 'fantasy',
  'exp-wedding-rajasthani-royal-v1': 'heritage',
  'exp-gsap-radha-krishna': 'heritage',
  'exp-gsap-shiva-parvati': 'heritage',
  'exp-birthday-superhero-v1': 'midnight_glow',
  'exp-birthday-dark-knight-v1': 'modern_noir',
  'exp-birthday-wonderland-v1': 'pastel_dream',
};

const ROYAL_ENGAGEMENT_TEMPLATE: ApiTemplate = {
  id: 'royal-engagement-experience',
  slug: 'royal-engagement-experience',
  name: 'Royal Engagement — Rajput Royal',
  occasionType: 'ENGAGEMENT',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A regal North Indian engagement invitation: animated rose-petal hero, ring-box reveal, both families, ring ceremony and a royal celebration programme with RSVP.',
  tags: ['engagement', 'royal', 'rajput', 'punjabi', 'animated', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: true,
    supportsMap: true,
    experienceRenderer: 'royal-engagement-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const ROYAL_MANDAP_PALACE_TEMPLATE: ApiTemplate = {
  id: 'royal-mandap-palace',
  slug: 'royal-mandap-palace',
  name: 'Royal Mandap Palace',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/royal-mandap-palace/jharokha.svg',
  isPremium: true,
  isActive: true,
  description:
    'Rajasthani palace plot-plane journey: jharokha gate, Ganesh courtyard, mandap pheras, function arcade, RSVP durbar — scroll depth with reduced-motion calm path.',
  tags: ['wedding', 'rajasthani', 'palace', 'mandap', 'plot-plane', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: true,
    supportsMap: true,
    supportsMultipleEvents: true,
    experienceRenderer: 'royal-mandap-palace',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const MARIGOLD_HAVELI_JOURNEY_TEMPLATE: ApiTemplate = {
  id: 'marigold-haveli-journey',
  slug: 'marigold-haveli-journey',
  name: 'Marigold Haveli Journey',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/marigold-haveli-journey/jali.svg',
  isPremium: true,
  isActive: true,
  description:
    'Warm daytime haveli plot-plane journey: jali gate, courtyard hero, mandap room, function verandah, RSVP courtyard — marigold petals on hero only.',
  tags: ['wedding', 'haveli', 'marigold', 'daytime', 'plot-plane', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: true,
    supportsMap: true,
    supportsMultipleEvents: true,
    experienceRenderer: 'marigold-haveli-journey',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const MODERN_FUSION_TEMPLATE: ApiTemplate = {
  id: 'modern-fusion-experience',
  slug: 'modern-fusion-experience',
  name: 'Modern Fusion — Urban Indian',
  occasionType: 'ENGAGEMENT',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A contemporary Indian engagement: glassmorphism hero, story timeline, events with dress codes, and RSVP with dietary preferences.',
  tags: ['modern', 'urban', 'fusion', 'minimalist', 'glassmorphism', 'engagement'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: false,
    supportsMap: true,
    experienceRenderer: 'modern-fusion-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * BirthdayCelebration — a code-rendered React template for birthday invitations.
 * Features: deep blue gradient, floating balloons, trio photo layout, happy members grid, activity timeline.
 * Rendering delegated to the frontend template `registry` by `id`.
 */
const BIRTHDAY_CELEBRATION_TEMPLATE: ApiTemplate = {
  id: 'birthday-celebration',
  slug: 'birthday-celebration',
  name: 'Birthday Celebration',
  occasionType: 'BIRTHDAY',
  previewImageUrl: null,
  isPremium: true,
  isActive: true,
  description:
    'Deep blue multi-section birthday invitation: floating balloons, trio photo layout, happy celebrants grid, and a vertical activity timeline.',
  tags: ['birthday', 'blue', 'animated', 'multi-section', 'celebration'],
  capabilities: { experienceRenderer: 'birthday-celebration' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * BirthdayPremium — the new premium, story-driven birthday template.
 * Distinct id from 'birthday-celebration' (which stays frozen). Adds a
 * full-screen hero, memory timeline, snapshot gallery, celebrants, countdown,
 * fun facts, and the interactive Wishes Wall + Memory Capsule.
 * Rendering delegated to the frontend template `registry` by `id`.
 */
const BIRTHDAY_PREMIUM_TEMPLATE: ApiTemplate = {
  id: 'birthday-premium',
  slug: 'birthday-premium',
  name: 'Birthday Premium',
  occasionType: 'BIRTHDAY',
  previewImageUrl: null,
  isPremium: true,
  isActive: true,
  description:
    'Premium story-driven birthday invitation: full-screen hero, memory timeline, snapshot gallery, celebrants, activity schedule, countdown, fun facts, interactive wishes wall, and memory capsule.',
  tags: ['birthday', 'blue', 'animated', 'multi-section', 'celebration', 'premium', 'story', 'interactive'],
  capabilities: { experienceRenderer: 'birthday-premium' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_BIRTHDAY_SUPERHERO_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-superhero-v1',
  slug: 'exp-birthday-superhero-v1',
  name: 'Superhero Avengers Assembly',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/cyberpunk_luxury_cake.jpg',
  isPremium: true,
  isActive: true,
  description:
    'Action-packed birthday experience: Stark Observatory, Cyberpunk Lounge, Iron Man vs Thanos game, and 360 panorama.',
  tags: ['cinematic', 'birthday', 'superhero', 'avengers', 'ironman', 'marvel', 'kids', 'premium', 'scroll-experience', 'cyberpunk', 'game'],
  capabilities: { experience: true, aiNative: true, stylePack: 'midnight_glow', supportsRSVP: true, experienceRenderer: 'birthday-superhero', aiImageGenDisabled: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_BIRTHDAY_DARK_KNIGHT_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-dark-knight-v1',
  slug: 'exp-birthday-dark-knight-v1',
  name: 'DC Dark Knight Cyber',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/batman_joker_birthday.jpg',
  isPremium: true,
  isActive: true,
  description:
    'Sleek Gotham cyber-vault birthday experience: Batcave command center, Bat-Signal spotlight beam, and Joker Cake Surprise.',
  tags: ['cinematic', 'birthday', 'dc', 'batman', 'dark-knight', 'gotham', 'joker', 'teens', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'modern_noir', supportsRSVP: true, experienceRenderer: 'birthday-dark-knight', aiImageGenDisabled: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_BIRTHDAY_WONDERLAND_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-wonderland-v1',
  slug: 'exp-birthday-wonderland-v1',
  // Matches the backend seed's name (scripts/seed-templates.ts) — that row already
  // avoids the "Barbie" trademark; this hardcoded fallback previously didn't, so the
  // template showed a different name depending on whether it loaded from the live API
  // or this offline fallback.
  name: 'Magical Fairy & Balloon Castle',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/barbie_princess_castle.jpg',
  isPremium: true,
  isActive: true,
  description:
    'Magical fairy garden & balloon castle birthday experience: princess tea party, magic wands, and living petal showers.',
  tags: ['cinematic', 'birthday', 'wonderland', 'princess', 'fairy', 'balloons', 'kids', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'pastel_dream', supportsRSVP: true, experienceRenderer: 'birthday-wonderland', aiImageGenDisabled: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Birthday Minimalist AI — clean, elegant experience powered by the `minimal` style pack.
 * Restored: these 3 were previously seeded as exp-birthday-*-v1 before the migration cleanup.
 */
const EXP_BIRTHDAY_MINIMALIST_AI_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-minimal-v1',
  slug: 'exp-birthday-minimal-v1',
  name: 'Birthday Minimalist',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/marathi/backdrop.jpg',
  isPremium: true,
  isActive: false,
  description:
    'A clean and elegant AI-generated birthday experience with soft linen tones, cinematic parallax, and smooth scroll interactions.',
  tags: ['ai-native', 'experience', 'minimal', 'birthday', 'elegant', 'clean', 'modern', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'minimal', supportsRSVP: true, experienceRenderer: 'experience-generic' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Birthday Premium Thematic AI — rich, cinematic experience with premium_thematic style pack.
 */
const EXP_BIRTHDAY_PREMIUM_THEMATIC_AI_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-premium-v1',
  slug: 'exp-birthday-premium-v1',
  name: 'Birthday Premium Thematic',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/cyberpunk_luxury_cake.jpg',
  isPremium: true,
  isActive: false,
  description:
    'A rich, cinematic AI-generated birthday experience with gold and crimson premium thematic styling, immersive scroll effects, and celebration moments.',
  tags: ['ai-native', 'experience', 'premium_thematic', 'birthday', 'cinematic', 'royal', 'premium', 'scroll-experience', 'thematic'],
  capabilities: { experience: true, aiNative: true, stylePack: 'premium_thematic', supportsRSVP: true, experienceRenderer: 'experience-generic' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Birthday Modern Vibrant AI — high-energy, vibrant experience with modern_vibrant style pack.
 */
const EXP_BIRTHDAY_MODERN_VIBRANT_AI_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-vibrant-v1',
  slug: 'exp-birthday-vibrant-v1',
  name: 'Birthday Modern Vibrant',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/cyberpunk_skyline_fireworks.jpg',
  isPremium: true,
  isActive: false,
  description:
    'A high-energy, vibrant AI-generated birthday experience with bright coral and gold tones, dynamic particles, and energetic scroll-driven celebration moments.',
  tags: ['ai-native', 'experience', 'modern_vibrant', 'birthday', 'vibrant', 'energetic', 'colorful', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'modern_vibrant', supportsRSVP: true, experienceRenderer: 'experience-generic' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const BIRTHDAY_NEON_BASH_TEMPLATE: ApiTemplate = {
  id: 'birthday-neon-bash',
  slug: 'birthday-neon-bash',
  name: 'Birthday Neon Bash',
  occasionType: 'BIRTHDAY',
  previewImageUrl: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'Ultra-modern interactive birthday invitation featuring 3D perspective transforms, real-time particle animation, neon color scheme (cyan/pink/purple), countdown timer, interactive memory carousel, guest wishes board, and RSVP.',
  tags: ['birthday', 'neon', 'modern', 'interactive', '3d-transforms', 'particle-effects'],
  capabilities: { experienceRenderer: 'birthday-neon-bash' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
};

const SEEMANTHAM_BABY_SHOWER_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_seemantham_001',
  slug: 'seemantham-baby-shower',
  name: 'Seemantham Baby Shower',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '/assets/seemantham-baby-shower/bg.jpg',
  isPremium: true,
  isActive: true,
  description: 'A traditional Indian baby shower (Seemantham) invitation featuring a floral arch, Lord Ganesha, and a beautiful illustration of the mother-to-be.',
  tags: ['baby-shower', 'seemantham', 'traditional', 'indian', 'floral'],
  capabilities: { experienceRenderer: 'seemantham-baby-shower' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const FLORAL_BLUSH_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_floral_blush_001',
  slug: 'baby-shower-elegant-floral',
  name: 'Elegant Floral Blush',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'Soft blush pink background with beautiful watercolor flowers, rose gold accents, and a luxurious feel.',
  tags: ['baby-shower', 'premium', 'floral', 'elegant', 'watercolor'],
  capabilities: { experienceRenderer: 'baby-shower-elegant-floral' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const ROYAL_PRINCE_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_royal_prince_002',
  slug: 'baby-shower-royal-prince',
  name: 'Royal Blue Baby Prince',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A sophisticated royal blue baby shower theme with hanging floral vines and a classic baby swing/cradle illustration.',
  tags: ['baby-shower', 'premium', 'royal-blue', 'sophisticated', 'cradle'],
  capabilities: { experienceRenderer: 'baby-shower-royal-prince' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const NEUTRAL_BOHO_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_neutral_boho_003',
  slug: 'baby-shower-neutral-boho',
  name: 'Neutral Boho',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A warm and earthy baby shower invitation featuring dried pampas grass, cream tones, and a soft minimalist rainbow.',
  tags: ['baby-shower', 'premium', 'boho', 'minimal', 'pampas'],
  capabilities: { experienceRenderer: 'baby-shower-neutral-boho' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const MOON_STARS_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_moon_stars_004',
  slug: 'baby-shower-moon-stars',
  name: 'Moon & Stars',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A dreamy champagne gold and warm white invitation with stars, soft clouds, and a cute sleeping teddy on the moon.',
  tags: ['baby-shower', 'premium', 'dreamy', 'moon', 'stars'],
  capabilities: { experienceRenderer: 'baby-shower-moon-stars' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const BOTANICAL_GARDEN_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_botanical_garden_005',
  slug: 'baby-shower-botanical-garden',
  name: 'Botanical Garden',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A fresh sage green and gold nature-inspired invitation decorated with delicate eucalyptus leaves and fine gold borders.',
  tags: ['baby-shower', 'premium', 'botanical', 'sage-green', 'gold'],
  capabilities: { experienceRenderer: 'baby-shower-botanical-garden' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const LUXURY_TEDDY_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_luxury_teddy_006',
  slug: 'luxury-teddy-blush',
  name: 'Luxury Teddy Blush',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A premium luxury baby shower invitation with soft blush pink watercolor, hand-painted floral bouquets, rose gold accents, and a charming teddy bear family on clouds.',
  tags: ['baby-shower', 'premium', 'luxury', 'watercolor', 'teddy-bear'],
  capabilities: { experienceRenderer: 'luxury-teddy-blush' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const ROYAL_CRADLE_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_royal_cradle_007',
  slug: 'royal-cradle-blue',
  name: 'Royal Cradle Blue',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A sophisticated baby shower invitation featuring white and powder blue watercolor, royal blue floral arrangements, silver framing accents, and a suspended cradle illustration.',
  tags: ['baby-shower', 'premium', 'royal', 'cradle', 'floral', 'blue'],
  capabilities: { experienceRenderer: 'royal-cradle-blue' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const LUXURY_BOHO_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_luxury_boho_008',
  slug: 'luxury-boho-sand',
  name: 'Luxury Boho Sand',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A modern luxury Boho baby shower invitation featuring warm beige and sand tones, pampas grass, palm leaves, thin gold borders, and a minimal teddy beside a crescent moon.',
  tags: ['baby-shower', 'premium', 'boho', 'sand', 'teddy', 'moon'],
  capabilities: { experienceRenderer: 'luxury-boho-sand' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const DREAMY_MOON_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_dreamy_moon_009',
  slug: 'dreamy-moon-gold',
  name: 'Dreamy Moon Gold',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A dreamy premium baby shower invitation using an ivory background with champagne gold gradients, featuring a sleeping teddy bear on a crescent moon, clouds, and white roses.',
  tags: ['baby-shower', 'premium', 'dreamy', 'moon', 'gold', 'watercolor'],
  capabilities: { experienceRenderer: 'dreamy-moon-gold' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const LUXURY_SAGE_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_luxury_sage_010',
  slug: 'luxury-sage-botanical',
  name: 'Luxury Sage Botanical',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A luxury botanical baby shower invitation featuring a soft sage green watercolor background with eucalyptus branches, white roses, and an elegant watercolor baby bassinet.',
  tags: ['baby-shower', 'premium', 'sage', 'botanical', 'roses', 'bassinet'],
  capabilities: { experienceRenderer: 'luxury-sage-botanical' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const MODERN_LUXURY_TEMPLATE: ApiTemplate = {
  id: 'tpl_baby_shower_modern_luxury_011',
  slug: 'baby-shower-modern-luxury',
  name: 'Modern Luxury',
  occasionType: 'BABY_SHOWER',
  previewImageUrl: '',
  isPremium: true,
  isActive: true,
  description: 'A completely modern, editorial, soft minimal premium template without floral elements. Features warm ivory and champagne gold accents.',
  tags: ['baby-shower', 'premium', 'modern', 'minimal', 'editorial'],
  capabilities: { experienceRenderer: 'ModernLuxuryBabyShower' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

export function makeSampleData(tpl: ApiTemplate): ApiInvitation {
  const base = {
    id: `preview-${tpl.id}`,
    publicSlug: null,
    templateId: tpl.id,
    occasionType: tpl.occasionType,
    hostedUrl: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // Birthday templates get a birthday-shaped sample. status is DRAFT so the
  // interactive sections render their sample content locally without firing
  // API calls against the fake preview id.
  if (tpl.occasionType === 'BIRTHDAY') {
    return {
      ...base,
      status: 'DRAFT',
      eventName: 'Aarav',
      eventDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(),
      eventVenue: 'Sunshine Banquet Hall, Mumbai',
      hostName: 'The Sharma Family',
      customMessage: "We can't wait to celebrate this special day with you!",
      rsvpLink: null,
      mediaUrls: [],
      customData: {
        birthday: {
          birthdayPersonName: 'Aarav',
          ageTurning: '7',
          celebrationTagline: 'Celebrating a Day Filled with Joy, Laughter & Beautiful Memories',
          hostNames: 'Mom & Dad',
          eventTime: '5:30 PM',
          venueName: 'Sunshine Banquet Hall',
          venueAddress: 'Sunshine Banquet Hall, Andheri West, Mumbai',
          snapshots: [
            { imageUrl: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?auto=format&fit=crop&w=600&q=80', caption: 'First Smile' },
            { imageUrl: 'https://images.unsplash.com/photo-1543248939-4296e1fea89b?auto=format&fit=crop&w=600&q=80', caption: 'First Steps' },
          ],
          celebrants: [
            { name: 'Aarav', relation: 'Birthday Star' },
            { name: 'Riya', relation: 'Big Sister' },
          ],
          activities: [
            { time: '5:30 PM', title: 'Guest Arrival', emoji: '🎈' },
            { time: '6:30 PM', title: 'Games & Fun', emoji: '🎮' },
            { time: '7:00 PM', title: 'Cake Cutting', emoji: '🎂' },
          ],
          funFacts: [
            { label: 'Favourite Food', value: 'Pizza 🍕' },
            { label: 'Favourite Colour', value: 'Blue 💙' },
          ],
        },
      },
    } as ApiInvitation;
  }

  if (tpl.occasionType === 'BABY_SHOWER') {
    return {
      ...base,
      status: 'DRAFT',
      eventName: 'Baby Shower',
      eventDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
      eventVenue: 'The Grand Conservatory, Address Here',
      hostName: 'The Harrington Family',
      customMessage: "Join us for a beautiful celebration!",
      rsvpLink: null,
      mediaUrls: [],
      customData: {
        babyShower: {
          parentsName: 'Olivia & Thomas Meadows',
          motherName: 'Olivia Meadows',
          fatherName: 'Thomas Meadows',
          motherInLawName: 'Amitha',
          fatherInLawName: 'Shyam Reddy',
        },
        baby: {
          motherName: 'Emma Harrington',
          hostedBy: 'The Harrington Family',
          eventDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          eventTime: '04:00 PM',
          venueName: 'The Stardust Pavilion',
          venueAddress: '555 Celestial Way, San Francisco, CA',
          rsvpName: 'Sophia Harrington',
          rsvpPhone: '+1 (555) 012-3456',
          message: 'Love you to the moon and back. Join us to celebrate the upcoming arrival of our little star!',
          dressCode: 'Champagne gold or warm white',
        }
      },
    } as ApiInvitation;
  }

  return {
    id: `preview-${tpl.id}`,
    publicSlug: null,
    templateId: tpl.id,
    occasionType: tpl.occasionType,
    status: 'PUBLISHED',
    hostedUrl: null,
    eventName: 'Your Event Name',
    eventDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    eventVenue: 'The Grand Venue, Mumbai',
    hostName: 'Your Name',
    customMessage: 'A heartfelt message to your guests goes here.',
    rsvpLink: '#',
    mediaUrls: [],
    customData: {
      wedding: {
        groom: {
          fullName: 'Rahul Sharma',
          familyPositionText: 'Second Son of Mr. & Mrs. Amit Sharma',
          fatherName: 'Amit Sharma',
          motherName: 'Neelam Sharma',
          homeAddress: 'Bandra West, Mumbai',
        },
        bride: {
          fullName: 'Priya Mehta',
          familyPositionText: 'Eldest Daughter of Mr. & Mrs. Sanjay Mehta',
          fatherName: 'Sanjay Mehta',
          motherName: 'Kavita Mehta',
          homeAddress: 'Juhu, Mumbai',
        },
        weddingEvents: [
          {
            title: 'Haldi',
            date: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '10:00 AM',
            venue: 'Sharma Residence, Bandra West',
            description: 'Turmeric & sunshine. Wear yellow!',
          },
          {
            title: 'Mehendi',
            date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '05:00 PM',
            venue: 'Courtyard Garden, Mumbai',
            description: 'An evening of henna, music, and family laughter.',
          },
          {
            title: 'Sangeet',
            date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '08:00 PM',
            venue: 'The Grand Ballroom, Mumbai',
            description: 'Dance, music, and celebration with loved ones.',
          },
          {
            title: 'Wedding Ceremony',
            date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '11:00 AM',
            venue: 'The Grand Venue, Mumbai',
            description: 'The ceremony, blessings, and sacred rituals.',
          },
          {
            title: 'Reception',
            date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '07:00 PM',
            venue: 'The Grand Venue, Mumbai',
            description: 'Dinner and celebrations for all.',
          },
        ],
        familyTree: [
          { id: 'gg-p', name: 'Late Shri Ramchandra Sharma', relationship: "Groom's Grandfather" },
          { id: 'gg-m', name: 'Smt. Savitri Sharma', relationship: "Groom's Grandmother", parentId: 'gg-p' },
          { id: 'gp', name: 'Mr. Amit Sharma', relationship: "Groom's Father", parentId: 'gg-p' },
          { id: 'groom-node', name: 'Rahul Sharma', relationship: 'Groom', parentId: 'gp' },
          { id: 'bg-p', name: 'Late Shri Mohan Mehta', relationship: "Bride's Grandfather" },
          { id: 'bp', name: 'Mr. Sanjay Mehta', relationship: "Bride's Father", parentId: 'bg-p' },
          { id: 'bride-node', name: 'Priya Mehta', relationship: 'Bride', parentId: 'bp' },
        ],
        familyInvitations: [
          { id: 'fi-1', name: 'Ramesh Sharma', relation: 'Paternal Uncle', message: 'Please join us and bless the couple on this auspicious occasion.' },
          { id: 'fi-2', name: 'Sunita Mehta', relation: 'Maternal Aunt', message: 'Your presence will make this day even more beautiful for our family.' },
          { id: 'fi-3', name: 'Rohan Sharma', relation: 'Elder Brother', message: 'Come celebrate the beginning of a beautiful journey with us!' },
        ],
        showFamilyInvitations: true,
        showOrganizers: true,
        organizers: [
          { id: 'org-1', name: 'Mr. Rajesh Sharma', designation: 'Main Organizer' },
          { id: 'org-2', name: 'Mrs. Sunita Sharma', designation: 'Co-Organizer' },
          { id: 'org-3', name: 'Mr. Amit Verma', designation: 'Coordinator' },
        ],
        showFamilyChildren: true,
        familyChildrenTitle: 'Our Little Stars',
        familyChildren: [
          { id: 'child-1', name: 'Aryan' },
          { id: 'child-2', name: 'Kiara' },
          { id: 'child-3', name: 'Aarav' },
          { id: 'child-4', name: 'Anaya' },
        ],
      },
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/** Sample invitation fields for a catalog preview. Does not create or update a draft. */
export function catalogPreviewData(tpl: ApiTemplate): DraftPreviewData {
  const sample = makeSampleData(tpl);
  return {
    templateId: tpl.id,
    occasionType: tpl.occasionType,
    eventName: sample.eventName,
    eventDate: sample.eventDate,
    eventVenue: sample.eventVenue,
    hostName: sample.hostName,
    customMessage: sample.customMessage,
    rsvpLink: sample.rsvpLink,
    mediaUrls: sample.mediaUrls,
    customData: (sample.customData as Record<string, unknown> | null | undefined) ?? null,
    template: tpl,
  };
}

type CatalogView = 'all' | 'wow';
const CATALOG_TABS: ReadonlyArray<{ id: CatalogView; label: string }> = [
  { id: 'all', label: 'All templates' },
  { id: 'wow', label: '✨ Wow · Live animated' },
];
const CATALOG_PANEL_ID = 'template-catalog-panel';

export function StepTemplate({ onNext, onBack, data, updateData }: StepProps) {
  const [templates, setTemplates] = useState<ApiTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewing, setPreviewing] = useState<ApiTemplate | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<{
    target: ApiTemplate;
    report: SwitchReport;
    nextCustomData: Record<string, unknown>;
    proceed: () => void;
  } | null>(null);
  const [view, setView] = useState<CatalogView>('all');
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // One play-slot pool per catalog: at most 2 (phone) / 3 (desktop) preview loops at once.
  const loopScheduler = useMemo(() => createSlotScheduler(loopConcurrencyCap()), []);

  useEffect(() => {
    // Code-rendered catalogue per occasion. DB templates (getTemplates) win by slug; these fill in whatever the
    // DB lacks and are the fallback when the API is down.
    const hardcodedFor = (occasion: string): ApiTemplate[] => {
      if (occasion === 'WEDDING') {
        return [
          WEDDING_PLOT_PLANE_TEMPLATE,
          ROYAL_MANDAP_PALACE_TEMPLATE,
          MARIGOLD_HAVELI_JOURNEY_TEMPLATE,
          VEDIC_MANDIR_DIVINE_TEMPLATE,
          EMERALD_NOIR_RECEPTION_TEMPLATE,
          SCRATCH_STD_GOLDENSAND_TEMPLATE,
          ROYAL_GANESH_INTERACTIVE_TEMPLATE,
          CINEMATIC_WEDDING_TEMPLATE,
          DEV21_WEDDING_TEMPLATE,
          LAGNA_PATRIKA_INTERACTIVE_TEMPLATE,
          STITCH_IMMERSIVE_WEDDING_TEMPLATE,
          MARATHI_WEDDING_CLASSIC_TEMPLATE,
          MARATHI_WEDDING_INTERACTIVE_TEMPLATE,
          TRADITIONAL_WEDDING_INTERACTIVE_TEMPLATE,
          SOUTH_INDIAN_TRADITION_TEMPLATE,
          NIKAH_ELEGANT_TEMPLATE,
          SIKH_ANAND_KARAJ_TEMPLATE,
          LAKE_PALACE_DESTINATION_TEMPLATE,
          VIDEO_ROYAL_01_TEMPLATE,
          VIDEO_KIRTAN_DIVINE_TEMPLATE,
          VIDEO_RADHA_KRISHNA_TEMPLATE,
          EXP_GSAP_RADHA_KRISHNA_TEMPLATE,
          EXP_GSAP_SHIVA_PARVATI_TEMPLATE,
          EXP_WEDDING_INDO_GHIBLI_TEMPLATE,
          EXP_WEDDING_PICHWAI_TEMPLATE,
          EXP_WEDDING_STORYBOOK_3D_TEMPLATE,
          MARATHI_AKSHATA_3D_TEMPLATE,
          KRISHNA_MEERA_3D_DIORAMA_TEMPLATE,
          EXP_CINEMATIC_ROYAL_TEMPLATE,
          EXP_CINEMATIC_MINIMAL_TEMPLATE,
          EXP_CINEMATIC_FANTASY_TEMPLATE,
          EXP_WEDDING_RAJASTHANI_ROYAL_TEMPLATE,
        ];
      }
      if (occasion === 'ENGAGEMENT') return [ROYAL_ENGAGEMENT_TEMPLATE, MODERN_FUSION_TEMPLATE];
      if (occasion === 'BIRTHDAY') {
        // Premium first (the showcase), then the frozen classic. The default stays on the classic.
        return [
          EXP_BIRTHDAY_SUPERHERO_TEMPLATE,
          EXP_BIRTHDAY_DARK_KNIGHT_TEMPLATE,
          EXP_BIRTHDAY_WONDERLAND_TEMPLATE,
          EXP_BIRTHDAY_MINIMALIST_AI_TEMPLATE,
          EXP_BIRTHDAY_PREMIUM_THEMATIC_AI_TEMPLATE,
          EXP_BIRTHDAY_MODERN_VIBRANT_AI_TEMPLATE,
          BIRTHDAY_NEON_BASH_TEMPLATE,
          BIRTHDAY_PREMIUM_TEMPLATE,
          BIRTHDAY_CELEBRATION_TEMPLATE,
        ];
      }
      if (occasion === 'BABY_SHOWER') {
        return [
          FLORAL_BLUSH_TEMPLATE,
          ROYAL_PRINCE_TEMPLATE,
          NEUTRAL_BOHO_TEMPLATE,
          MOON_STARS_TEMPLATE,
          BOTANICAL_GARDEN_TEMPLATE,
          LUXURY_TEDDY_TEMPLATE,
          ROYAL_CRADLE_TEMPLATE,
          LUXURY_BOHO_TEMPLATE,
          DREAMY_MOON_TEMPLATE,
          LUXURY_SAGE_TEMPLATE,
          MODERN_LUXURY_TEMPLATE,
          SEEMANTHAM_BABY_SHOWER_TEMPLATE,
        ];
      }
      return [VIDEO_ROYAL_01_TEMPLATE, VIDEO_KIRTAN_DIVINE_TEMPLATE];
    };
    const preferredIdFor = (occasion: string): string | undefined =>
      occasion === 'WEDDING'
        ? CINEMATIC_WEDDING_TEMPLATE.id
        : occasion === 'ENGAGEMENT'
          ? ROYAL_ENGAGEMENT_TEMPLATE.id
          : occasion === 'BIRTHDAY'
            ? BIRTHDAY_CELEBRATION_TEMPLATE.id
            : occasion === 'BABY_SHOWER'
              ? FLORAL_BLUSH_TEMPLATE.id
              : undefined;
    const applyTemplates = (all: ApiTemplate[], preselect = false) => {
      const active = all.filter((t) => t.isActive !== false);
      setTemplates(active);
      if (preselect && !data.templateId && active.length > 0) {
        const preferred = preferredIdFor(data.occasionType);
        updateData({ templateId: (active.find((t) => t.id === preferred) ?? active[0]).id });
      }
    };

    setError('');
    const curated = ['WEDDING', 'ENGAGEMENT', 'BIRTHDAY', 'BABY_SHOWER'].includes(data.occasionType);
    let cancelled = false;
    if (curated) {
      // Curated occasions render instantly from the code catalogue (no spinner, works offline); DB templates
      // are layered on top once they arrive — a DB row replaces the code entry with the same slug/id.
      setLoading(false);
      applyTemplates(hardcodedFor(data.occasionType), true);
      getTemplates(data.occasionType, 150)
        .then((apiTemplates) => {
          if (cancelled || !Array.isArray(apiTemplates) || apiTemplates.length === 0) return;
          setTemplates((prev) => {
            // Replace code entries in place (no remounts); DB-only templates go first.
            const bySlug = new Map(apiTemplates.map((t) => [t.slug, t]));
            const byId = new Map(apiTemplates.map((t) => [t.id, t]));
            const seen = new Set<string>();
            const merged = prev.flatMap((t) => {
              const db = bySlug.get(t.slug) ?? byId.get(t.id);
              if (!db) return [t];
              seen.add(db.id);
              return db.isActive === false ? [] : [db];
            });
            const added = apiTemplates.filter((t) => !seen.has(t.id) && t.isActive !== false);
            return [...added, ...merged];
          });
        })
        .catch((err) => console.warn('Backend templates API failed; showing the built-in catalogue:', err));
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    getTemplates(data.occasionType, 150)
      .then((apiTemplates) => applyTemplates(apiTemplates))
      .catch((err) => {
        console.warn('Backend templates API failed, using resilient hardcoded catalog fallback:', err);
        applyTemplates(hardcodedFor(data.occasionType));
      })
      .finally(() => setLoading(false));
  }, [data.occasionType]);

  useEffect(() => {
    trackEvent('TEMPLATE_VIEW', 'wizard');
  }, []);

  const selected = data.templateId;
  // "Wow" = animated templates that have a pre-recorded preview loop (npm run record:previews).
  const wowTemplates = useMemo(
    () => templates.filter((tpl) => isWowTemplate(tpl.id) && getTemplatePreview(tpl.id)),
    [templates],
  );
  const hasWow = wowTemplates.length > 0;
  const activeView: CatalogView = hasWow ? view : 'all';
  const shownTemplates = activeView === 'wow' ? wowTemplates : templates;

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = CATALOG_TABS.length - 1;
    let next = -1;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next < 0) return;
    event.preventDefault();
    setView(CATALOG_TABS[next].id);
    tabRefs.current[next]?.focus();
  };

  /** Per-template style tag — no generic rule, so it's a lookup + id-suffix fallback. */
  function resolveStyle(tpl: ApiTemplate): string | undefined {
    let style = EXPERIENCE_STYLE_BY_TEMPLATE_ID[tpl.id];
    if (!style) {
      if (tpl.id.includes('-minimal') || tpl.id.endsWith('-minimal-v1')) {
        style = 'minimal';
      } else if (tpl.id.includes('-premium_thematic') || tpl.id.endsWith('-premium_thematic-v1')) {
        style = 'premium_thematic';
      } else if (tpl.id.includes('-modern_vibrant') || tpl.id.endsWith('-modern_vibrant-v1')) {
        style = 'modern_vibrant';
      }
    }
    return style;
  }

  /**
   * Record whether this template is driven by the Type-A DSL pipeline — the honest
   * signal is the template's own `capabilities.aiNative` flag, never the id string.
   * (A past fix string-matched `exp-cinematic-`, which silently excluded every other
   * AI-native template — birthday flagships, Rajasthani — from ever building a DSL.
   * Do not reintroduce that pattern here.)
   */
  function resolveIsAiNative(tpl: ApiTemplate): boolean {
    const caps = (tpl.capabilities ?? {}) as { aiNative?: boolean };
    return caps.aiNative === true;
  }

  /** First-ever pick, or re-clicking the already-selected template — nothing to reconcile. */
  function applyFreshSelection(tpl: ApiTemplate) {
    const style = resolveStyle(tpl);
    const isAiNative = resolveIsAiNative(tpl);
    updateData({
      templateId: tpl.id,
      ...(style || isAiNative ? {
        customData: {
          ...data.customData,
          experience: {
            ...(data.customData?.experience as object | undefined),
            ...(style ? { template: style } : {}),
            aiNative: isAiNative,
            occasion: tpl.occasionType.toLowerCase(),
          },
        } as any,
      } : {}),
    });
  }

  /**
   * Pick `tpl` as the wizard's template. `proceed` (usually onNext, or closing the
   * preview modal + onNext) only runs immediately for a fresh pick — a genuine switch
   * away from an already-chosen template instead opens the impact dialog, and
   * `proceed` runs from confirmPendingSwitch() once the user confirms.
   */
  /**
   * Whether there's anything a switch could actually lose. The wizard
   * pre-selects a default templateId on mount (see the useEffect above) so
   * the picker isn't empty — that default is not a user decision, and a
   * user's very first click must not be treated as a "switch" away from it.
   * Content-based rather than a mount/ref flag: this component remounts on
   * every AnimatePresence step transition (see Wizard.tsx), so any local
   * "has the user confirmed yet" flag would reset the moment they navigate
   * away and back, wrongly skipping the dialog on a real second switch.
   */
  function hasMeaningfulContent(d: typeof data): boolean {
    if (d.eventName?.trim() || d.hostName?.trim() || d.eventVenue?.trim() || d.customMessage?.trim()) {
      return true;
    }
    if (d.mediaUrls && d.mediaUrls.length > 0) return true;
    const cd = d.customData as Record<string, unknown> | undefined;
    if (cd && Object.keys(cd).some((k) => k !== 'experience' && k !== '_parked')) return true;
    return false;
  }

  function requestTemplateChange(tpl: ApiTemplate, proceed: () => void) {
    const previousTemplateId = data.templateId;
    if (!previousTemplateId || previousTemplateId === tpl.id || !hasMeaningfulContent(data)) {
      applyFreshSelection(tpl);
      proceed();
      return;
    }

    const fromTemplate = templates.find((t) => t.id === previousTemplateId);
    const { customData: nextCustomData, report } = switchTemplate({
      customData: data.customData as Record<string, unknown> | undefined,
      fromTemplateId: previousTemplateId,
      toTemplateId: tpl.id,
      fromSchemaRaw: fromTemplate?.dataSchema,
      toSchemaRaw: tpl.dataSchema,
      occasionType: data.occasionType,
      isAiNative: resolveIsAiNative(tpl),
      style: resolveStyle(tpl),
    });

    setPendingSwitch({ target: tpl, report, nextCustomData, proceed });
  }

  function confirmPendingSwitch() {
    if (!pendingSwitch) return;
    const { target, nextCustomData, proceed } = pendingSwitch;
    updateData({ templateId: target.id, customData: nextCustomData as any });
    setPendingSwitch(null);
    proceed();
  }

  function cancelPendingSwitch() {
    if (pendingSwitch && data.templateId) {
      trackEvent('TEMPLATE_CHANGE_CANCELLED', 'wizard', pendingSwitch.target.id, undefined, {
        from_template_id: data.templateId,
        to_template_id: pendingSwitch.target.id,
      });
    }
    setPendingSwitch(null);
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-brand-deep mb-4">Choose a Template</h1>
        <p className="text-on-surface-variant font-medium max-w-2xl">
          Select a starting point for your invitation. Click Preview to see a live render.
        </p>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        </div>
      )}

      {!loading && error && (
        <p className="text-status-failed font-medium mb-8">{error}</p>
      )}

      {!loading && !error && templates.length === 0 && (
        <div className="text-center py-16 text-on-surface-variant font-medium">
          No templates available for this occasion yet. More coming soon!
        </div>
      )}

      {!loading && !error && hasWow && (
        <div className="w-full flex flex-col items-center gap-2 mb-8">
          <div
            role="tablist"
            aria-label="Template views"
            className="inline-flex max-w-full rounded-full border border-outline-variant/30 bg-surface-low p-1 shadow-inner"
          >
            {CATALOG_TABS.map((tab, index) => {
              const active = activeView === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  id={`template-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={CATALOG_PANEL_ID}
                  tabIndex={active ? 0 : -1}
                  onClick={() => setView(tab.id)}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                  className={cn(
                    'min-h-11 rounded-full px-4 sm:px-6 text-sm font-bold whitespace-nowrap transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                    active ? 'bg-primary text-white shadow' : 'text-on-surface-variant hover:text-on-surface',
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs font-medium text-on-surface-variant">These invites move — tap to see them live</p>
        </div>
      )}

      {!loading && !error && templates.length > 0 && (
        <LoopSlotProvider value={loopScheduler}>
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full mb-16"
          {...(hasWow
            ? { id: CATALOG_PANEL_ID, role: 'tabpanel', 'aria-labelledby': `template-tab-${activeView}` }
            : {})}
        >
          {shownTemplates.map((tpl) => {
            const isSelected = selected === tpl.id;
            const isWow = isWowTemplate(tpl.id);
            const clip = activeView === 'wow' ? getTemplatePreview(tpl.id) : undefined;
            return (
              <motion.div
                layoutId={tpl.id}
                key={tpl.id}
                className={cn(
                  'group relative rounded-3xl border-2 overflow-hidden flex flex-col transition-all',
                  isSelected
                    ? 'border-primary bg-surface shadow-2xl ring-4 ring-primary/10'
                    : 'border-surface-low bg-surface shadow-sm hover:shadow-xl hover:border-outline-variant',
                )}
              >
                {(tpl.isPremium || isWow) && (
                  <div className="absolute top-4 left-4 z-20 flex items-center gap-1.5 pointer-events-none">
                    {tpl.isPremium && (
                      <div className="bg-amber-500 text-white text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                        Premium
                      </div>
                    )}
                    {isWow && <LiveBadge />}
                  </div>
                )}
                {isSelected && (
                  <div className="absolute top-4 right-4 z-20 bg-primary text-white rounded-full p-1 shadow-lg">
                    <Icons.CheckCircle size={18} />
                  </div>
                )}
                <div className="relative w-full aspect-[4/5] bg-surface-low overflow-hidden">
                  {clip ? (
                    <WowPreviewCard template={tpl} clip={clip} onOpen={() => setPreviewing(tpl)} />
                  ) : tpl.occasionType === 'WEDDING' || tpl.previewImageUrl ? (
                    <TemplateCardPreview template={tpl} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-on-surface-variant/20">
                      <Icons.Calendar size={64} />
                    </div>
                  )}
                </div>
                <div className="p-6 flex flex-col gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-on-surface">{hostFacingCopy(tpl.name) || tpl.name}</h3>
                    <p className="text-xs text-on-surface-variant capitalize">
                      {tpl.occasionType.replace('_', ' ').toLowerCase()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1 font-bold"
                      onClick={() => setPreviewing(tpl)}
                    >
                      <Icons.Search size={14} /> Preview
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        trackEvent('TEMPLATE_SELECTED', 'wizard', tpl.id, undefined, { template_id: tpl.id });
                        // A swap still counts as "changed my mind" — the friction GA4 measures (#364).
                        trackTemplateChange(tpl.id);
                        requestTemplateChange(tpl, onNext);
                      }}
                    >
                      Use this design
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        </LoopSlotProvider>
      )}

      <div className="w-full flex items-center pt-8 border-t border-outline-variant/10">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <Icons.ArrowLeft size={18} /> Back
        </Button>
      </div>

      <FullScreenPreviewDialog
        open={previewing != null}
        onClose={() => setPreviewing(null)}
        closeLabel="Back to Wizard"
        continueLabel="Select This Template"
        continueTone="commit"
        badge="Sample preview"
        onContinue={() => {
          if (!previewing) return;
          trackEvent('TEMPLATE_SELECTED', 'wizard', previewing.id, undefined, { template_id: previewing.id });
          // A swap still counts as "changed my mind" — the friction GA4 measures (#364).
          trackTemplateChange(previewing.id);
          const tpl = previewing;
          // Close the preview first — a genuine switch shows the impact dialog over the picker.
          setPreviewing(null);
          requestTemplateChange(tpl, onNext);
        }}
      >
        {previewing && (
          registry.has(previewing.id) ? (
            <TemplateRenderer
              templateId={previewing.id}
              data={makeSampleData(previewing)}
            />
          ) : previewing.renderMode === 'json-renderer' && previewing.layoutSchema ? (
            <JsonTemplateRenderer
              schema={previewing.layoutSchema}
              data={makeSampleData(previewing)}
              theme={previewing.themeConfig}
              mode="preview"
            />
          ) : previewing.renderMode === 'experience' || previewing.id.startsWith('exp-') ? (
            <SceneExperienceRenderer manifest={resolveExperienceManifest(previewing, makeSampleData(previewing))} />
          ) : previewing.occasionType === 'WEDDING' ? (
            <CinematicWeddingExperience data={makeSampleData(previewing)} />
          ) : (
            <div className="flex items-center justify-center min-h-[70vh] text-stone-400 text-sm px-8 text-center">
              Preview not available yet. Use this design to continue with your own details.
            </div>
          )
        )}
      </FullScreenPreviewDialog>

      <SwitchTemplateDialog
        isOpen={pendingSwitch !== null}
        targetTemplateName={pendingSwitch?.target.name ?? ''}
        report={pendingSwitch?.report ?? { carriedOver: [], parked: [], missing: [] }}
        onConfirm={confirmPendingSwitch}
        onCancel={cancelPendingSwitch}
      />
    </div>
  );
}
